import React from "react";
import {Avatar, Box, Chip, Grid, Paper, Tab, Tabs, Typography} from "@mui/material";
import {
    ArticleOutlined,
    DescriptionOutlined,
    InfoOutlined,
    ReceiptLongOutlined,
    TrendingUpOutlined,
    WarningAmberOutlined,
} from "@mui/icons-material";
import type {Supplier360} from "../supplier360DemoData";
import {
    formatCompactCurrency,
    formatPercent,
    riskScoreBand,
    supplierStatusColor,
    supplierStatusLabel,
} from "../supplier360Utils";
import {OverviewTab} from "./tabs/OverviewTab";
import {TransactionsTab} from "./tabs/TransactionsTab";
import {ContractsTab} from "./tabs/ContractsTab";
import {NewsTab} from "./tabs/NewsTab";
import {OpportunitiesTab} from "./tabs/OpportunitiesTab";
import {RisksTab} from "./tabs/RisksTab";

type Props = { supplier: Supplier360 };

type KpiTile = { label: string; value: React.ReactNode; sub?: string };

export const SupplierDetailPanel: React.FC<Props> = ({supplier}) => {
    const [tab, setTab] = React.useState(0);
    const band = riskScoreBand(supplier.riskScore);

    // Reset to the overview tab whenever the selected supplier changes.
    React.useEffect(() => setTab(0), [supplier.id]);

    const kpis: KpiTile[] = [
        {
            label: 'Annual spend',
            value: formatCompactCurrency(supplier.annualSpend, supplier.currency),
            sub: `${formatPercent(supplier.spendTrendPct, true)} YoY`,
        },
        {label: 'Transactions', value: supplier.transactionCount.toLocaleString(), sub: 'last 12 months'},
        {label: 'Active contracts', value: supplier.activeContracts, sub: `${supplier.contracts.length} on record`},
        {label: 'On-time delivery', value: formatPercent(supplier.onTimeDeliveryPct), sub: 'target 95%'},
        {label: 'Risk score', value: `${supplier.riskScore}/100`, sub: `${band.label} risk`},
        {label: 'Open opportunities', value: supplier.opportunities.filter(o => o.status !== 'realized').length, sub: 'identified'},
    ];

    return <Box className="s360-detail">
        <Paper variant="outlined" sx={{borderRadius: '12px', overflow: 'hidden'}}>
            <Box className="s360-detail-header">
                <Avatar className="s360-avatar" variant="rounded" sx={{bgcolor: supplier.logoColor}}>
                    {supplier.name.slice(0, 2).toUpperCase()}
                </Avatar>
                <Box className="s360-detail-title">
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Typography variant="h1" sx={{fontSize: 22}}>{supplier.name}</Typography>
                        <Chip size="small" color={supplierStatusColor(supplier.status)} label={supplierStatusLabel(supplier.status)}/>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {supplier.category} · {supplier.country} · partner since {supplier.relationshipSince}
                    </Typography>
                </Box>
            </Box>

            <Box className="s360-kpi-grid" sx={{px: 2.5, pb: 2.5}}>
                <Grid container spacing={2}>
                    {kpis.map(k => (
                        <Grid size={{xs: 6, sm: 4, lg: 2}} key={k.label}>
                            <Box className="s360-kpi">
                                <div className="s360-kpi-label">{k.label}</div>
                                <div className="s360-kpi-value">{k.value}</div>
                                {k.sub && <div className="s360-kpi-sub">{k.sub}</div>}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Paper>

        <Box className="s360-tabs">
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile>
                <Tab icon={<InfoOutlined/>} iconPosition="start" label="Overview"/>
                <Tab icon={<ReceiptLongOutlined/>} iconPosition="start" label={`Transactions (${supplier.transactions.length})`}/>
                <Tab icon={<DescriptionOutlined/>} iconPosition="start" label={`Contracts (${supplier.contracts.length})`}/>
                <Tab icon={<ArticleOutlined/>} iconPosition="start" label={`News (${supplier.news.length})`}/>
                <Tab icon={<TrendingUpOutlined/>} iconPosition="start" label={`Opportunities (${supplier.opportunities.length})`}/>
                <Tab icon={<WarningAmberOutlined/>} iconPosition="start" label={`Risks (${supplier.risks.length})`}/>
            </Tabs>

            <Box className="s360-tab-panel">
                {tab === 0 && <OverviewTab supplier={supplier}/>}
                {tab === 1 && <TransactionsTab supplier={supplier}/>}
                {tab === 2 && <ContractsTab supplier={supplier}/>}
                {tab === 3 && <NewsTab supplier={supplier}/>}
                {tab === 4 && <OpportunitiesTab supplier={supplier}/>}
                {tab === 5 && <RisksTab supplier={supplier}/>}
            </Box>
        </Box>
    </Box>;
};
